import ComponentManager from "../../componentFramework";
import MessageTooltip from "./MessageTooltip";

/**
 * This:
 * 1) Applies style class to the field which the message is for.
 * 2) Turns the message in to a tooltip by using MessageTooltip
 * 3) Dispatches two DOM events which bubble from the field this message is for: "fieldMessageAdded" and "fieldMessageRemoved"
 */
@ComponentManager.register()
export default class Message {
	public static componentStyleClass = "message";
	private static readonly styleClassForFieldAreaIsSetup = "js-message--isSetupAsTooltip"; // Used if this is inside a fieldArea
	private static readonly styleClassModifierPrefix = "message--";
	private static readonly styleClassFieldHasMessage = "js-message";

	private messageTooltip: MessageTooltip | undefined;

	constructor(private readonly rootElement: HTMLElement) {
	}

	public static containsMessage(contextElement: HTMLElement) {
		return contextElement.querySelector(`.${Message.styleClassFieldHasMessage}`) !== null;
	}

	@ComponentManager.on("constructed")
	protected componentConstructed() {
		const field = this.getField();
		if (field !== null) {
			// I want this done before "ready" so other components can use containsMessage() earlier.
			this.setFieldsStyleClass(field);
		}
	}

	protected setFieldsStyleClass(field: HTMLElement) {
		// Mark field with style classes which indicate message severity (e.g. js-message--error)
		if (!field.classList.contains(Message.styleClassFieldHasMessage)) {
			field.classList.add(Message.styleClassFieldHasMessage);
			for (let i = 0; i < this.rootElement.classList.length; i++) {
				const styleClass = this.rootElement.classList[i];
				if (styleClass !== undefined && styleClass.indexOf(Message.styleClassModifierPrefix) === 0) {
					field.classList.add("js-" + styleClass);
				}
			}
		}
	}

	@ComponentManager.on("ready")
	protected componentReady() {
		const field = this.getField();
		if (field === null) {
			return;
		}

		// Call again in case the field could not be found at the "construct" phase. (It should, but just in case.)
		this.setFieldsStyleClass(field);

		// Construct a tooltip version of this message (this follows the user's mouse)
		this.messageTooltip = new MessageTooltip(
			field,
			{
				messageElement: this.rootElement
			}
		);

		const fieldArea = Message.getFieldArea(field);
		if (fieldArea !== null) {
			// Remove "legacy" style classes from field area which are not needed.
			for (let i = 0; i < fieldArea.classList.length;) {
				const itemI = fieldArea.classList.item(i);
				if (itemI !== null && itemI.startsWith("contains")) {
					fieldArea.classList.remove(itemI);
				} else {
					i++;
				}
			}

			// Remove PrimeFace's style class for marking errors. We don't need those.
			fieldArea.querySelectorAll(".ui-state-error").forEach(element => element.classList.remove("ui-state-error"));

			// Mark fieldArea, if there is one.
			fieldArea.classList.add(Message.styleClassForFieldAreaIsSetup);
		}

		// Dispatch a DOM event that field has an error
		field.dispatchEvent(
			new CustomEvent(
				"fieldMessageAdded", {
				bubbles: true,
				cancelable: false,
				detail: {
					field: field,
					message: this
				}
			})
		);
	}

	@ComponentManager.on("removed")
	protected componentRemoved() {
		const field = this.getField();
		if (field !== null) {
			// Clean up style class on field and fieldArea
			field.classList.remove(Message.styleClassFieldHasMessage);
			for (let i = 0; i < field.classList.length; i++) {
				const styleClass = field.classList[i];
				if (styleClass !== undefined && styleClass.indexOf("js-" + Message.styleClassModifierPrefix) === 0) {
					field.classList.remove(styleClass);
				}
			}

			const fieldArea = Message.getFieldArea(field);
			if (fieldArea !== null) {
				fieldArea.classList.remove(Message.styleClassForFieldAreaIsSetup);
			}

			// Dispatch a DOM event that field's message has been removed
			field.dispatchEvent(
				new CustomEvent(
					"fieldMessageRemoved", {
						bubbles: true,
						cancelable: false,
						detail: {
							field: field,
							message: this
						}
					})
			);
		}

		if (this.messageTooltip !== undefined) {
			this.messageTooltip.destroy();
			this.messageTooltip = undefined;
		}
	}

	private static getFieldArea(field: HTMLElement | null) {
		return field !== null ? field.closest(".fieldArea") : null;
	}

	private getField(): HTMLElement | null {
		const fieldId = this.rootElement.getAttribute("data-message-for");
		return fieldId !== null ? this.rootElement.ownerDocument.getElementById(fieldId) : null;
	}
}
